import { useCallback, useEffect, useState } from 'react';

export const STAFF_TEMP_NAV_DISCLOSURE_STORAGE_KEY = 'ystnj.shell.staff.tempNavExpanded';
export const PARENT_TEMP_NAV_DISCLOSURE_STORAGE_KEY = 'ystnj.shell.parent.tempNavExpanded';

const canUseStorage = (): boolean => (
  typeof window !== 'undefined' && Boolean(window.localStorage)
);

const readStoredExpandedState = (storageKey: string, defaultExpanded: boolean): boolean => {
  if (!canUseStorage()) {
    return defaultExpanded;
  }
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === '1' || raw === 'true') {
      return true;
    }
    if (raw === '0' || raw === 'false') {
      return false;
    }
  } catch {
    // Ignore storage read failures and fall back to the default.
  }
  return defaultExpanded;
};

const writeStoredExpandedState = (storageKey: string, expanded: boolean): void => {
  if (!canUseStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(storageKey, expanded ? '1' : '0');
  } catch {
    // Ignore storage write failures; the disclosure still works for the session.
  }
};

export const useTempNavDisclosure = (input: {
  storageKey: string;
  forceOpen: boolean;
  defaultExpanded?: boolean;
}) => {
  const { storageKey, forceOpen, defaultExpanded = false } = input;
  const [rememberedExpanded, setRememberedExpanded] = useState<boolean>(() => (
    readStoredExpandedState(storageKey, defaultExpanded)
  ));

  useEffect(() => {
    writeStoredExpandedState(storageKey, rememberedExpanded);
  }, [rememberedExpanded, storageKey]);

  const toggle = useCallback(() => {
    setRememberedExpanded((current) => (forceOpen ? current : !current));
  }, [forceOpen]);

  return {
    expanded: forceOpen || rememberedExpanded,
    lockedOpen: forceOpen,
    rememberedExpanded,
    setRememberedExpanded,
    toggle,
  };
};
